/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, May 2022
 * @flow
 */

import React from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import View from './views';
import API from '../../requests';
import FireboardUtils from '../../util/fireboard-util';
import { getPopupConfig } from '../../components/Popup';

class EmployeeSectionAssignment extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      popup: undefined,
      sectionEmployees: [],
      employeeSections: [],
      primarySelectedSection: null,
      primarySelectedEmployee: null,
      secondarySelectedSections: new Map(),
      secondarySelectedEmployees: new Map(),
      sectionView: false,
      excludeTerminated: true,
      jobAllowsAssignment: true,
      jobsMap: new Map(),
      assigned: false,
      sortByName: true,
      filteredEmployees: [],
      searchEmployeeValue: '',
      revenueCentersMap: new Map(),
    };
  }

  componentDidMount() {
    const { history, reduxProps: { storeInfo, deviceInfo } } = this.props;
    if (!storeInfo.data || !deviceInfo.deviceExternalId) {
      history.push({
        pathname: '/',
        state: { deviceTypeId: 15 }
      });
      return;
    }

    this.getStationEmployees();
  }

  getStationEmployees = () => {
    const { reduxProps: { storeInfo } } = this.props;
    const { id: storeId, jobs = [], revenueCenters } = storeInfo?.data || {};
    let jobsMap = new Map();
    jobs.forEach(job => jobsMap.set(job.id, job));
    let revenueCentersMap = new Map();
    revenueCenters.forEach(revenueCenter => revenueCentersMap.set(revenueCenter.id, revenueCenter.name));

    this.getPopup('spinner', 'Fetching employee assignments', null);
    API.getSectionEmployee({ storeId })
      .then(response => {
        const { sectionEmployees, employeeSections } = response?.data;
        sectionEmployees.sort(this.sortBySectionName);
        this.setState({
          sectionEmployees,
          employeeSections,
          filteredEmployees: employeeSections,
          popup: undefined,
          jobsMap,
          revenueCentersMap,
        });
        FireboardUtils.addNetworkLogs(response);
      })
      .catch(error => this.getErrorPopup(error));
  };

  pad = (value) => {
    let str = String(value);
    let size = 10;
    while (size > 0) {
      str = '0' + str;
      size--;
    }
    return str;
  };

  sortBySectionName = (a, b) => {
    const sectionName1 = FireboardUtils.addPrefixWithZero(a.Section.name ? a.Section.name : '', 10).toLowerCase();
    const sectionName2 = FireboardUtils.addPrefixWithZero(b.Section.name ? b.Section.name : '', 10).toLowerCase();
    if (sectionName1 < sectionName2) {
      return -1;
    }
    if (sectionName1 > sectionName2) {
      return 1;
    }
    return 0;
  };

  sortByEmployeeLastName = (a, b) => {
    const name1 = this.pad(a.employee.lastName ? a.employee.lastName : '').toLowerCase();
    const name2 = this.pad(b.employee.lastName ? b.employee.lastName : '').toLowerCase();
    if (name1 < name2) {
      return -1;
    }
    if (name1 > name2) {
      return 1;
    }
    return 0;
  };

  sortByEmployeePOSID = (a, b) => {
    const posId1 = FireboardUtils.addPrefixWithZero(a.employee.posId ? a.employee.posId : '', 10).toLowerCase();
    const posId2 = FireboardUtils.addPrefixWithZero(b.employee.posId ? b.employee.posId : '', 10).toLowerCase();
    if (posId1 < posId2) {
      return -1;
    }
    if (posId1 > posId2) {
      return 1;
    }
    return 0;
  };

  onSelectPrimary = (selectedSection, selectedEmployee) => {
    const { sectionView } = this.state;
    if (sectionView) {
      const { primarySelectedSection, secondarySelectedSections } = this.state;
      secondarySelectedSections.clear();
      if (primarySelectedSection && primarySelectedSection?.id === selectedSection?.id) {
        this.setState({ primarySelectedSection: null, secondarySelectedSections });
      } else {
        this.setState({ primarySelectedSection: selectedSection, secondarySelectedSections });
      }
    } else {
      const { primarySelectedEmployee, secondarySelectedEmployees } = this.state;
      secondarySelectedEmployees.clear();
      if (primarySelectedEmployee && primarySelectedEmployee?.id === selectedEmployee?.id) {
        this.setState({ primarySelectedEmployee: null, secondarySelectedEmployees });
      } else {
        this.setState({ primarySelectedEmployee: selectedEmployee, secondarySelectedEmployees });
      }
    }
  };

  onSelectSecondaryEmployee = employee => {
    let { secondarySelectedEmployees } = this.state;
    if (secondarySelectedEmployees.has(employee.id)) {
      secondarySelectedEmployees.delete(employee.id);
    } else {
      secondarySelectedEmployees = new Map();
      secondarySelectedEmployees.set(employee.id, employee);
    }
    this.setState({ secondarySelectedEmployees });
  };

  onSelectSecondarySection = section => {
    const { secondarySelectedSections } = this.state;
    if (secondarySelectedSections.has(section.id)) {
      secondarySelectedSections.delete(section.id);
    } else {
      secondarySelectedSections.set(section.id, section);
    }
    this.setState({ secondarySelectedSections });
  };

  assignEmployeeToSection = () => {
    const { primarySelectedSection, secondarySelectedEmployees } = this.state;
    const employeeIds = Array.from(secondarySelectedEmployees.keys()) || [];
    const callback = () => {
      const { reduxProps: { storeInfo } } = this.props;
      const { id: storeId } = storeInfo?.data || {};
      if (primarySelectedSection && primarySelectedSection?.id && employeeIds.length > 0) {
        const payload = {
          storeId,
          sectionId: primarySelectedSection.id,
          employeeId: employeeIds[0],
        }
        this.getPopup('spinner', 'Assigning employee', null);
        API.assignEmployeeToSection(payload)
          .then(response => {
            this.resetStations(response.data);
            toast.success("Employee assigned successfully", { autoClose: 1000 });
          })
          .catch(error => this.getErrorPopup(error));
      }
    };
    return { callback };
  };

  assignSectionToEmployee = () => {
    const { primarySelectedEmployee, secondarySelectedSections } = this.state;
    const sectionIds = Array.from(secondarySelectedSections.keys()) || [];
    const callback = () => {
      const { reduxProps: { storeInfo } } = this.props;
      const { id: storeId } = storeInfo?.data || {};
      if (primarySelectedEmployee && primarySelectedEmployee?.id && sectionIds.length > 0) {
        const payload = {
          storeId,
          employeeId: primarySelectedEmployee.id,
          sectionIds: sectionIds
        }
        this.getPopup('spinner', 'Assigning sections', null);
        API.assignSectionToEmployee(payload)
          .then(response => {
            this.resetStations(response.data);
            toast.success("Sections assigned successfully", { autoClose: 1000 });
          })
          .catch(error => this.getErrorPopup(error));
      }
    };
    return { callback };
  };

  onMerge = () => {
    const { sectionView } = this.state;
    let callbackToBeInvoked = null;
    if (sectionView) {
      const { callback } = this.assignEmployeeToSection();
      callbackToBeInvoked = callback;
    } else {
      const { callback } = this.assignSectionToEmployee();
      callbackToBeInvoked = callback;
    }
    callbackToBeInvoked && callbackToBeInvoked();
  };

  removeSectionAssignment = (section, employee) => {
    const { reduxProps: { storeInfo } } = this.props;
    const { id: storeId } = storeInfo?.data || {};
    const payload = {
      storeId,
      employeeId: employee?.id,
      sectionIds: section ? [section?.id] : [],
    };
    this.getPopup('spinner', 'Removing section assignment', null);
    API.unAssignSectionFromEmployee(payload)
      .then(response => {
        this.resetStations(response.data);
        toast.success("Section assignment removed successfully", { autoClose: 1000 });
      })
      .catch(error => this.getErrorPopup(error));
  };

  removeEmployeeAssignment = (section, employee) => {
    const { reduxProps: { storeInfo } } = this.props;
    const { id: storeId } = storeInfo?.data || {};
    const payload = {
      storeId,
      employeeId: employee?.id,
      sectionId: section?.id,
    };
    this.getPopup('spinner', 'Removing employee assignment', null);
    API.unAssignEmployeeFromSection(payload)
      .then(response => {
        this.resetStations(response.data);
        toast.success("Employee assignment removed successfully", { autoClose: 1000 });
      })
      .catch(error => this.getErrorPopup(error));
  };

  onUnAssign = (section, employee) => {
    const { sectionView } = this.state;
    if (sectionView) {
      this.removeEmployeeAssignment(section, employee);
    } else {
      this.removeSectionAssignment(section, employee);
    }
  };

  resetStations = response => {
    const { secondarySelectedSections, secondarySelectedEmployees } = this.state;
    const { sectionEmployees, employeeSections } = response;
    secondarySelectedSections.clear();
    secondarySelectedEmployees.clear();
    sectionEmployees.sort(this.sortBySectionName);
    this.setState({
      sectionEmployees,
      employeeSections,
      filteredEmployees: employeeSections,
      popup: undefined,
      primarySelectedSection: null,
      primarySelectedEmployee: null,
      secondarySelectedSections,
      secondarySelectedEmployees,
      searchEmployeeValue: '',
    });
  };

  getPopup = (type, heading, message, callback) => {
    const popup =
      type === 'spinner'
        ? getPopupConfig('spinner')
        : getPopupConfig('error', () => {
          callback && callback();
          this.setState({ popup: undefined });
        });
    if (heading) {
      popup.heading = heading;
    }
    if (message) {
      popup.message = message;
    }
    this.setState({ popup });
  };

  closePopup = () => {
    this.setState({ popup: undefined });
  };

  getErrorPopup = (error) => {
    const errorMsg = error.response
      ? error.response.data
      : error.message;
    FireboardUtils.addNetworkLogs(error.response);
    this.getPopup('error', null, errorMsg);
  };

  goBack = () => {
    const { history } = this.props;
    history && history.goBack();
  };

  changeView = () => {
    this.setState(prevState => ({
      sectionView: !prevState.sectionView
    }));
  };

  toggleCheckbox = key => event => {
    this.setState({ [key]: event.target.checked });
  };

  onChangeSortByName = () => {
    this.setState(prevState => ({
      sortByName: !prevState.sortByName
    }));
  };

  onSearchEmployee = event => {
    const val = event.target.value;
    const value = val.toLowerCase();
    const { employeeSections } = this.state;
    const filteredEmployees = employeeSections.filter(eachEmployee => {
      const { employee } = eachEmployee;
      const firstName = employee?.firstName || '';
      const lastName = employee?.lastName || '';
      const posId = employee?.posId || '';
      if ((firstName && firstName.toLowerCase().includes(value)) ||
        (lastName && lastName.toLowerCase().includes(value)) ||
        (posId && posId.toLowerCase().includes(value))
      ) {
        return true;
      }
      return false;
    });
    this.setState({
      searchEmployeeValue: val,
      filteredEmployees,
    });
  };

  get viewProps() {
    const {
      popup,
      sectionEmployees,
      primarySelectedEmployee,
      primarySelectedSection,
      secondarySelectedEmployees,
      secondarySelectedSections,
      filteredEmployees,
      sectionView,
      excludeTerminated,
      jobAllowsAssignment,
      jobsMap,
      assigned,
      sortByName,
      searchEmployeeValue,
      revenueCentersMap,
    } = this.state || {};
    const employeeList = sortByName ? filteredEmployees.sort(this.sortByEmployeeLastName) : filteredEmployees.sort(this.sortByEmployeePOSID);

    return {
      popup,
      sectionList: sectionEmployees,
      employeeList: employeeList,
      primarySelectedEmployee,
      primarySelectedSection,
      secondarySelectedEmployees,
      secondarySelectedSections,
      sectionView,
      excludeTerminated,
      jobAllowsAssignment,
      jobsMap: jobsMap,
      assigned,
      sortByName,
      searchEmployeeValue,
      revenueCentersMap,
      goBack: this.goBack,
      closePopup: this.closePopup,
      onMerge: this.onMerge,
      onSelectPrimary: this.onSelectPrimary,
      onSelectSecondarySection: this.onSelectSecondarySection,
      onSelectSecondaryEmployee: this.onSelectSecondaryEmployee,
      toggleCheckbox: this.toggleCheckbox,
      changeView: this.changeView,
      onChangeSortByName: this.onChangeSortByName,
      onSearchEmployee: this.onSearchEmployee,
      onUnAssign: this.onUnAssign,
    };
  };

  render() {
    const viewProps = this.viewProps;
    return <View {...viewProps} />;
  }
}

const mapStateToProps = (reduxProps) => ({
  reduxProps,
});

export default connect(mapStateToProps, null)(EmployeeSectionAssignment);
